import type { Readable } from "svelte/store"

/**
 * Transforms a store into a promise, awaiting a checker function
 * @param store Store to watch
 * @param checker Function to validate whether to fire
 * @returns
 */
export function awaitStore<T>(
    store: Readable<T>,
    checker: (value: T) => boolean = (v) => v == true,
    /**
     * How many times we want to try before failing
     * 0 = it has to be true right away
     * 1 = it can be false once
     * 2 = it can be false twice...
     */
    attempts = Infinity,
) {
    return new Promise<T>((res, rej) => {
        let attempt = 0
        const unsub = store.subscribe(async (v) => {
            if (checker(v)) {
                res(v)
                Promise.resolve().then(() => unsub())
            } else {
                attempt++
                if (attempts < attempt) {
                    rej(v)
                    Promise.resolve().then(() => unsub())
                }
            }
        })
    })
}

/**
 * Transforms a store into a promise, awaiting a checker function
 * @param store Store to watch
 * @param checker Function to validate whether to fire
 * @returns
 */
export function awaitStoreTruthy<T>(store: Readable<T>, attempts = Infinity): Promise<NonNullable<T>> {
    return awaitStore(store, (v) => !!v, attempts) as Promise<NonNullable<T>>
}
